/**
 * Client half of the Scripture AI chat.
 *
 * Talks to our own Express proxy at /api/chat — never to api.anthropic.com
 * directly. A browser can't hold an API key safely, and the Anthropic API
 * doesn't allow browser origins anyway.
 *
 * The proxy streams Server-Sent Events; each `data:` line is one JSON frame:
 *   {type:'delta', text}   incremental assistant text
 *   {type:'done',  stopReason}
 *   {type:'error', message, code}
 */

export class ChatError extends Error {
  constructor(message, { code = 'unknown', retryable = false } = {}) {
    super(message);
    this.name = 'ChatError';
    this.code = code;
    this.retryable = retryable;
  }
}

/**
 * Static-preview builds (`VITE_PREVIEW=true npm run build`) ship the front end
 * without the API server. Say so directly rather than letting the fetch fail
 * and reporting it as a network error the viewer can't act on.
 */
const IS_STATIC_PREVIEW = import.meta.env?.VITE_PREVIEW === 'true';

/**
 * @param {object}   opts
 * @param {Array}    opts.messages  [{role:'user'|'assistant', content:string}]
 * @param {object=}  opts.verse     verse currently on screen, sent as context
 * @param {AbortSignal=} opts.signal
 * @param {(chunk:string)=>void} opts.onDelta called for each token chunk
 * @returns {Promise<{text:string, stopReason:string}>}
 */
export async function streamChat({ messages, verse, signal, onDelta }) {
  if (IS_STATIC_PREVIEW) {
    throw new ChatError(
      'Scripture AI runs through the app’s own API server, which isn’t part of this static preview. Everything else here is fully working — clone the repo, add an ANTHROPIC_API_KEY and run `npm run dev` to try the chat.',
      { code: 'preview', retryable: false },
    );
  }

  let response;
  try {
    response = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, verse: verse ?? null }),
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new ChatError(
      'Could not reach the Scripture AI service. Check that the API server is running.',
      { code: 'network', retryable: true },
    );
  }

  if (!response.ok || !response.body) {
    // The proxy sends JSON errors for non-streaming failures.
    let detail = null;
    try {
      detail = await response.json();
    } catch {
      /* body wasn't JSON */
    }
    throw new ChatError(detail?.message ?? `Request failed (${response.status}).`, {
      code: detail?.code ?? String(response.status),
      retryable: response.status === 429 || response.status >= 500,
    });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let stopReason = 'end_turn';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line.
      let split;
      while ((split = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);

        const dataLine = frame
          .split('\n')
          .find((line) => line.startsWith('data:'));
        if (!dataLine) continue;

        let payload;
        try {
          payload = JSON.parse(dataLine.slice(5).trim());
        } catch {
          continue; // ignore a malformed frame rather than killing the stream
        }

        if (payload.type === 'delta') {
          text += payload.text;
          onDelta?.(payload.text);
        } else if (payload.type === 'done') {
          stopReason = payload.stopReason ?? 'end_turn';
        } else if (payload.type === 'error') {
          throw new ChatError(payload.message ?? 'The assistant hit an error.', {
            code: payload.code ?? 'stream',
            retryable: !!payload.retryable,
          });
        }
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }

  if (stopReason === 'refusal') {
    throw new ChatError(
      'The assistant declined to answer that one. Try rephrasing your question.',
      { code: 'refusal' },
    );
  }

  return { text, stopReason };
}

/** Suggested openers shown when the chat is empty. */
export const CHAT_SUGGESTIONS = [
  'What’s the context around this verse?',
  'Explain this passage in simple terms.',
  'How does this connect to the rest of Scripture?',
  'Give me a question to reflect on today.',
];
